import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;

    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);

    return userData;
  }, []);

  const register = useCallback(async (
    name,
    email,
    password,
    role = 'user',
    siteName = '',
    siteLocation = '',
    isIndividual = false,
    companyId = null,
    companyName = null,
  ) => {
    const response = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      role,
      site_name: siteName,
      site_location: siteLocation,
      is_individual: isIndividual,
      company_id: companyId,
      company_name: companyName,
    });
    const { token: newToken, user: userData } = response.data;

    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);

    return userData;
  }, []);

  const refreshUser = useCallback(async () => {
    if (token) {
      await fetchUser();
    }
  }, [token, fetchUser]);

  const isAdmin = useCallback(() => user?.role === 'admin', [user]);
  const isManager = useCallback(() => user?.role === 'manager', [user]);
  const isAdminOrManager = useCallback(
    () => user?.role === 'admin' || user?.role === 'manager',
    [user],
  );

  // Memoized context value — prevents needless re-renders of every consumer on each provider render.
  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      login,
      register,
      logout,
      isAdmin,
      isManager,
      isAdminOrManager,
      refreshUser,
    }),
    [user, token, loading, login, register, logout, isAdmin, isManager, isAdminOrManager, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
